//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { BodyErrorText } from '@components/atoms/text/Text';
import IItemMetadata from '@models/IItemMetadata';
import { Permissions } from '@models/Permissions';
import { useModeratorUiaAccess } from '@moderation-mode/hooks/useModeratorUiaAccess';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { ShareWithoutProductTagsModal } from '@uia/share/ShareWithoutProductTagsModal';
import { useShareMediaMutation } from '@uia/share/hooks/useShareMediaMutation';

interface Props {
  isDisabled?: boolean;
  metadata: IItemMetadata;
}

export const UIAShareAction = ({ isDisabled = false, metadata }: Props) => {
  const {
    isShareFlowEnabled,
    canShareModerationMedia,
  } = useModeratorUiaAccess();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isForceReviewed, setIsForceReviewed] = useState(true);
  const submissionInFlightRef = useRef(false);

  const { hasPendingShare, isError, isPending, isSuccess, mutateAsync, reset } =
    useShareMediaMutation(metadata.rootCuid);

  const submitShare = async (forceReview: boolean) => {
    if (
      !canShareModerationMedia ||
      isDisabled ||
      isSuccess ||
      hasPendingShare() ||
      submissionInFlightRef.current
    ) {
      return;
    }

    submissionInFlightRef.current = true;

    try {
      await mutateAsync({
        cuids: [metadata.rootCuid],
        isForceReviewed: forceReview,
      });

      setIsConfirmationOpen(false);
    } catch {
      // The mutation shows the global error toast.
      // Keep the confirmation open and preserve the checkbox for retry.
    } finally {
      submissionInFlightRef.current = false;
    }
  };

  const handleShare = () => {
    if (
      !canShareModerationMedia ||
      isDisabled ||
      isSuccess ||
      hasPendingShare() ||
      submissionInFlightRef.current
    ) {
      return;
    }

    reset();

    if (metadata.productTags.length === 0) {
      setIsForceReviewed(true);
      setIsConfirmationOpen(true);
      return;
    }

    void submitShare(false);
  };

  const handleCancel = () => {
    if (submissionInFlightRef.current) {
      return;
    }

    setIsConfirmationOpen(false);
    reset();
  };

  if (!isShareFlowEnabled) {
    return null;
  }

  return (
    <>
      <ShareFooter>
        <ShareActions>
          {isError && !isConfirmationOpen && (
            <BodyErrorText role='alert'>
              Failed to share this item. Please try again.
            </BodyErrorText>
          )}

          <UIAFooter.RequiredPermissionsActionButton
            isDisabled={isDisabled || isPending || isConfirmationOpen || isSuccess}
            onClick={handleShare}
            permissionNames={[Permissions.ModerationView]}
            trackingId={UIATrackingIds.SHARE}
          >
            {isPending ? 'Sharing…' : isSuccess ? 'Shared' : 'Share'}
          </UIAFooter.RequiredPermissionsActionButton>
        </ShareActions>
      </ShareFooter>

      <ShareWithoutProductTagsModal
        canShareModerationMedia={canShareModerationMedia}
        isError={isError}
        isForceReviewed={isForceReviewed}
        isOpen={isConfirmationOpen}
        isPending={isPending}
        onCancel={handleCancel}
        onForceReviewedChange={setIsForceReviewed}
        onShare={() => void submitShare(isForceReviewed)}
      />
    </>
  );
};

const ShareFooter = styled(UIAFooter)`
  flex-shrink: 0;
`;

const ShareActions = styled(UIAFooter.Actions)(
  ({ theme }) => `
    margin: 0 ${theme.spacings[3]};
    padding: ${theme.spacings[3]} 0;
    border-bottom: 0;
  `,
);
